"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import Image from "next/image"
import { Menu, Moon, Sun } from "lucide-react"
import { useDarkMode } from "@/contexts/DarkModeContext"
import { UserMenu } from "./UserMenu"
import styles from "./google-buttons.module.css"

type UseAuthLike = {
  data: any
  status: "loading" | "authenticated" | "unauthenticated"
}

export function Topbar({
  auth,
  onOpenMobileMenu,
  onSignOut,
}: {
  auth: UseAuthLike
  onOpenMobileMenu: () => void
  onSignOut: () => void
}) {
  const { data: session, status } = auth
  const { isDark, toggleDarkMode } = useDarkMode()

  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const [googleConnected, setGoogleConnected] = useState<boolean | null>(null)

  useEffect(() => {
    const fetchGoogleStatus = async () => {
      if (status !== "authenticated") return
      try {
        const res = await fetch("/api/profile")
        if (res.ok) {
          const data = await res.json()
          setGoogleConnected(!!data.googleCalendarId)
        }
      } catch (error) {
        console.error("Failed to fetch Google Calendar status:", error)
      }
    }
    fetchGoogleStatus()
  }, [status])

  const handleConnectGoogle = () => {
    window.location.href = "/api/auth/google/connect"
  }

  return (
    <nav className="fixed top-0 left-0 right-0 h-16 bg-white/80 dark:bg-zinc-900/80 backdrop-blur-xl border-b border-zinc-200/70 dark:border-zinc-800 z-50 transition-all">
      <div className="flex items-center justify-between h-full px-4 lg:px-6">
        <div className="flex items-center gap-4">
          <button
            onClick={onOpenMobileMenu}
            className="lg:hidden p-2 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded-xl transition-colors"
            aria-label="Otwórz menu"
          >
            <Menu className="w-6 h-6 text-gray-600 dark:text-zinc-400" />
          </button>
          <Link href="/" className="relative group transition-transform active:scale-95">
            <Image
              src="/logo_text_blue.png?v=3"
              alt="Logo"
              width={140}
              height={45}
              className="h-10 w-auto object-contain"
              priority
            />
          </Link>
        </div>

        <div className="flex items-center gap-3 sm:gap-4">
          {/* Dark Mode Toggle */}
          <button
            onClick={toggleDarkMode}
            className="p-2.5 rounded-xl bg-gray-50 dark:bg-zinc-800/50 text-gray-500 dark:text-zinc-400 hover:bg-gray-100 dark:hover:bg-zinc-800 transition-all active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
            aria-label="Zmień motyw"
          >
            {isDark ? <Sun className="w-5 h-5 text-amber-400" /> : <Moon className="w-5 h-5 text-blue-600" />}
          </button>

          {/* Google Calendar Button */}
          {googleConnected === null ? (
            <div className="hidden md:flex w-10 h-10 items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
            </div>
          ) : googleConnected ? (
            <button className={"hidden md:flex " + styles.gsiMaterialButtonConnected} title="Połączono z Google Calendar">
              <div className={styles.gsiMaterialButtonConnectedIcon}>
                <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" style={{ display: "block" }}>
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                  <path fill="none" d="M0 0h48v48H0z"></path>
                </svg>
              </div>
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center border-2 border-white dark:border-zinc-900 shadow-sm">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="w-3 h-3 text-white">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
            </button>
          ) : (
            <button onClick={handleConnectGoogle} className={"hidden md:flex " + styles.gsiMaterialButtonNavbar} title="Połącz z Google Calendar">
              <div className={styles.gsiMaterialButtonNavbarState}></div>
              <div className={styles.gsiMaterialButtonNavbarContentWrapper}>
                <div className={styles.gsiMaterialButtonNavbarIcon}>
                  <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" style={{ display: "block" }}>
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                    <path fill="none" d="M0 0h48v48H0z"></path>
                  </svg>
                </div>
                <span className={styles.gsiMaterialButtonNavbarContents}>Połącz</span>
              </div>
            </button>
          )}

          <UserMenu
            session={session}
            isLoading={status === "loading"}
            isOpen={isUserMenuOpen}
            setIsOpen={setIsUserMenuOpen}
            onSignOut={onSignOut}
          />
        </div>
      </div>
    </nav>
  )
}
